import { computed, effect, signal, type Signal } from "@preact/signals";
import i18n from "i18next";
import type { LoginManager } from "../managers/LoginManager";
import {
  getProfileConfigValue,
  saveProfileConfigValue,
} from "../managers/ProfileConfigSync";
import * as z from "zod/v4";
import type { CasualOSManager } from "./OsManager";
import type { NavigationManager } from "./NavigationManager";
import { isMinimalEmbedUrl } from "./EmbedMode";
import { SYSTEM_THEME_ID, type ThemeHighlightColor } from "./ThemeManager";
import { parseNumber } from "./Utils";

export type BookOrientation = "traditional" | "tanakh";
export type UISize = "S" | "M" | "L" | "XL";
export type TextAlignment = "unset" | "left" | "center" | "right";
export type TextSectionId = "bookTitle" | "heading" | "verse";

export type TextSize = "XS" | "S" | "M" | "L" | "XL" | "XXL";

export type SettingsPresetId = "minimal" | "full";

interface FontSizePanelsPreset {
  fontSize: TextSize;
  disablePanels: boolean;
}

const FULL_CONFIG: FontSizePanelsPreset = {
  disablePanels: false,
  fontSize: "M",
};

const MINIMAL_CONFIG: FontSizePanelsPreset = {
  disablePanels: true,
  fontSize: "M",
};

const DEFAULT_CONFIG_PRESETS: Record<SettingsPresetId, FontSizePanelsPreset> = {
  minimal: MINIMAL_CONFIG,
  full: FULL_CONFIG,
};

const DEFAULT_SETTINGS_PRESET: SettingsPresetId = "full";

function getPresetConfig(
  settingsPreset: SettingsPresetId
): FontSizePanelsPreset {
  return DEFAULT_CONFIG_PRESETS[settingsPreset] ?? FULL_CONFIG;
}

function parseSettingsPreset(value: unknown): SettingsPresetId {
  if (value === "minimal" || value === "full") {
    return value;
  }

  return DEFAULT_SETTINGS_PRESET;
}

function parseFontSize(value: unknown, fallback: TextSize): TextSize {
  if (typeof value !== "string") {
    return fallback;
  }

  const normalized = value.trim().toUpperCase();
  switch (normalized) {
    case "XS":
    case "S":
    case "M":
    case "L":
    case "XL":
    case "XXL":
      return normalized;
    default:
      return fallback;
  }
}

export interface SelectionUIBehavior {
  showSelectedItems: boolean;
  showHighlightColors: boolean;
  showIconText: boolean;
}

export interface ScriptureElementsBehavior {
  showHeadings: boolean;
  showVerseNumbers: boolean;
  showFootnotes: boolean;
  showHighlights: boolean;
  showRedLettering: boolean;
}

export interface DiscoveredContentBehavior {
  showContent: boolean;
}

export interface TextSectionConfig {
  font: string;
  weight: string;
  color: string;
  marginVertical: number;
  marginHorizontal: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  alignment: TextAlignment;
  /** Line height for the verse section. Other sections ignore this. */
  lineHeight?: number;
}

export const VERSE_LINE_HEIGHT_OPTIONS: number[] = [1.5, 1.7, 2, 2.5];
export const DEFAULT_VERSE_LINE_HEIGHT = 1.7;

export type TextConfig = Record<TextSectionId, TextSectionConfig>;

export interface ToolbarCustomization {
  /** Tool IDs that should be hidden from the toolbar. */
  hidden: string[];
  /** Tool IDs in preferred display order. IDs not listed keep their default priority after the ordered ones. */
  order: string[];
}
export type VerseSelectionEffects = "default" | "enabled" | "disabled";

export interface AppSettings {
  fontSize: TextSize;
  disablePanels: boolean;
  bookOrientation: BookOrientation;
  uiSize: UISize;
  selectionUI: SelectionUIBehavior;
  scriptureElements: ScriptureElementsBehavior;
  discoveredContent: DiscoveredContentBehavior;
  textConfig: TextConfig;
  toolbar: ToolbarCustomization;
  keepScreenAwake: boolean;
  /**
   * Whether picking a Bible translation written in another language may offer
   * to switch the UI to that language. Cleared by the prompt's "Never ask
   * again" option.
   */
  askToSwitchUiLanguage: boolean;
  /** User-added custom highlight colors (hex strings, max 3). */
  customHighlightColors: string[];
  /** Max width of the chapter content in ch. */
  scriptureWidth: number;
  /** Selected theme preset id (owned/consumed by ThemeManager). */
  themeId: string;
  /** User color overrides layered on top of the selected theme preset. */
  customTheme: Record<string, string>;
  /** User highlight-color overrides layered on top of the preset highlights. */
  customHighlights: Record<string, Partial<ThemeHighlightColor>>;
  /**
   * Whether the compact discover panel sits beside the scripture text
   * (true, the default) when there's room, or is forced below it, after the
   * license notice, at any viewport width (false). Flipped by the
   * "discover-content-panel" quick tool; consumed by `BibleReadingManager`'s
   * per-tab `discoverContentPanelInline` signal.
   */
  discoverContentPanelInline: boolean;
  verseSelectionEffects?: VerseSelectionEffects;
}

export const DEFAULT_SCRIPTURE_WIDTH = 68; // ch
export const MAX_SCRIPTURE_WIDTH = 192; // ch
export const MIN_SCRIPTURE_WIDTH = 24; // ch

export const AppSettingsSchema = z.object({
  fontSize: z.enum(["XS", "S", "M", "L", "XL", "XXL"]),
  disablePanels: z.boolean(),
  bookOrientation: z.enum(["traditional", "tanakh"]),
  uiSize: z.enum(["S", "M", "L", "XL"]),
  selectionUI: z.object({
    showSelectedItems: z.boolean(),
    showHighlightColors: z.boolean(),
    showIconText: z.boolean(),
  }),
  scriptureElements: z.object({
    showHeadings: z.boolean(),
    showVerseNumbers: z.boolean(),
    showFootnotes: z.boolean(),
    showHighlights: z.boolean(),
    showRedLettering: z.boolean(),
  }),
  discoveredContent: z
    .object({
      showContent: z.boolean(),
    })
    .default({ showContent: true }),
  verseSelectionEffects: z
    .enum(["default", "enabled", "disabled"])
    .default("default"),
  textConfig: z.object({
    bookTitle: z.object({
      font: z.string(),
      weight: z.string(),
      color: z.string(),
      marginVertical: z.number(),
      marginHorizontal: z.number(),
      bold: z.boolean(),
      italic: z.boolean(),
      underline: z.boolean(),
      alignment: z.enum(["unset", "left", "center", "right"]),
      lineHeight: z.number().optional(),
    }),
    heading: z.object({
      font: z.string(),
      weight: z.string(),
      color: z.string(),
      marginVertical: z.number(),
      marginHorizontal: z.number(),
      bold: z.boolean(),
      italic: z.boolean(),
      underline: z.boolean(),
      alignment: z.enum(["unset", "left", "center", "right"]),
      lineHeight: z.number().optional(),
    }),
    verse: z.object({
      font: z.string(),
      weight: z.string(),
      color: z.string(),
      marginVertical: z.number(),
      marginHorizontal: z.number(),
      bold: z.boolean(),
      italic: z.boolean(),
      underline: z.boolean(),
      alignment: z.enum(["unset", "left", "center", "right"]),
      lineHeight: z.number().optional(),
    }),
  }),
  toolbar: z.object({
    hidden: z.array(z.string()),
    order: z.array(z.string()),
  }),
  keepScreenAwake: z.boolean(),
  // Defaulted rather than required so settings exported before this option
  // existed still import.
  askToSwitchUiLanguage: z.boolean().default(true),
  customHighlightColors: z.array(z.string()).max(3),
  scriptureWidth: z
    .number()
    .min(MIN_SCRIPTURE_WIDTH)
    .max(MAX_SCRIPTURE_WIDTH)
    .default(DEFAULT_SCRIPTURE_WIDTH),
  themeId: z.string(),
  customTheme: z.record(z.string(), z.string()),
  customHighlights: z.record(
    z.string(),
    z.object({
      color: z.string().optional(),
      fontColor: z.string().optional(),
      wordsOfJesusFontColor: z.string().optional(),
    })
  ),
  // Defaulted rather than required so settings exported before this option
  // existed still import.
  discoverContentPanelInline: z.boolean().default(true),
});

export const MOBILE_SCRIPTURE_MARGIN = 5;

export const MAX_CUSTOM_HIGHLIGHT_COLORS = 3;

const TAG_FONT_SIZE = "app.fontSize";
const TAG_DISABLE_PANELS = "app.disablePanels";
const TAG_BOOK_ORIENTATION = "app.bookOrientation";
const TAG_UI_SIZE = "app.uiSize";
const TAG_SELECTION_UI = "app.selectionUI";
const TAG_SCRIPTURE_ELEMENTS = "app.scriptureElements";
const TAG_DISCOVERED_CONTENT = "app.discoveredContent";
const TAG_TEXT_CONFIG = "app.textConfig";
const TAG_TOOLBAR = "app.toolbarConfig";
const TAG_KEEP_AWAKE = "app.keepScreenAwake";
const TAG_ASK_TO_SWITCH_UI_LANGUAGE = "app.askToSwitchUiLanguage";
const TAG_CUSTOM_HIGHLIGHT_COLORS = "app.customHighlightColors";
const TAG_SCRIPTURE_WIDTH = "app.scriptureWidth";
const TAG_THEME_ID = "app.themeId";
const TAG_CUSTOM_THEME = "app.customTheme";
const TAG_CUSTOM_HIGHLIGHTS = "app.customHighlights";
const TAG_DISCOVER_CONTENT_PANEL_INLINE = "app.discoverContentPanelInline";
const TAG_VERSE_SELECTION_EFFECTS = "app.verseSelectionEffects";

// Profile.config keys are stored unprefixed.
const PROFILE_FONT_SIZE = "fontSize";
const PROFILE_DISABLE_PANELS = "disablePanels";
const PROFILE_BOOK_ORIENTATION = "bookOrientation";
const PROFILE_UI_SIZE = "uiSize";
const PROFILE_SELECTION_UI = "selectionUI";
const PROFILE_SCRIPTURE_ELEMENTS = "scriptureElements";
const PROFILE_DISCOVERED_CONTENT = "discoveredContent";
const PROFILE_TEXT_CONFIG = "textConfig";
const PROFILE_TOOLBAR = "toolbarConfig";
const PROFILE_KEEP_AWAKE = "keepScreenAwake";
const PROFILE_ASK_TO_SWITCH_UI_LANGUAGE = "askToSwitchUiLanguage";
const PROFILE_CUSTOM_HIGHLIGHT_COLORS = "customHighlightColors";
const PROFILE_SCRIPTURE_WIDTH = "scriptureWidth";
const PROFILE_THEME_ID = "themeId";
const PROFILE_CUSTOM_THEME = "customTheme";
const PROFILE_CUSTOM_HIGHLIGHTS = "customHighlights";
const PROFILE_DISCOVER_CONTENT_PANEL_INLINE = "discoverContentPanelInline";
const PROFILE_VERSE_SELECTION_EFFECTS = "verseSelectionEffects";

export const TEXT_FONT_OPTIONS: { value: string; label: string }[] = [
  { value: "'Newsreader', serif", label: "Newsreader" },
  { value: "'Plus Jakarta Sans', sans-serif", label: "Plus Jakarta Sans" },
  { value: "system-ui, sans-serif", label: "System UI" },
  { value: "'Helvetica Neue', sans-serif", label: "Helvetica Neue" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Inter', sans-serif", label: "Inter" },
];

export const TEXT_WEIGHT_OPTIONS: { value: string; label: string }[] = [
  { value: "700", label: "bold" },
  { value: "400", label: "regular" },
  { value: "300", label: "light" },
];

// export const TEXT_SECTION_LABELS: Record<TextSectionId, string> = {
//   bookTitle: "Book title",
//   heading: "Heading",
//   verse: "Verse",
// };

const DEFAULT_SELECTION_UI: SelectionUIBehavior = {
  showSelectedItems: true,
  showHighlightColors: true,
  showIconText: true,
};

const DEFAULT_SCRIPTURE_ELEMENTS: ScriptureElementsBehavior = {
  showHeadings: true,
  showVerseNumbers: true,
  showFootnotes: true,
  showHighlights: true,
  showRedLettering: true,
};

const DEFAULT_DISCOVERED_CONTENT: DiscoveredContentBehavior = {
  showContent: true,
};

/**
 * Empty `color` means "follow the active theme". The reader CSS reads
 * `--sb-{section}-font-color` directly; the text editor's color setting
 * writes to that same variable as a body inline override (so it beats the
 * theme's body-scoped CSS rule). Switching theme presets clears the
 * override — see `resetTextColors`.
 */
const DEFAULT_TEXT_CONFIG: TextConfig = {
  bookTitle: {
    font: "'Newsreader', serif",
    weight: "700",
    color: "",
    marginVertical: 12,
    marginHorizontal: 0,
    bold: true,
    italic: false,
    underline: false,
    alignment: "unset",
  },
  heading: {
    font: "'Plus Jakarta Sans', sans-serif",
    weight: "700",
    color: "",
    marginVertical: 18,
    marginHorizontal: 0,
    bold: true,
    italic: false,
    underline: false,
    alignment: "unset",
  },
  verse: {
    font: "'Plus Jakarta Sans', sans-serif",
    weight: "500",
    color: "",
    marginVertical: 0,
    marginHorizontal: 0,
    bold: false,
    italic: false,
    underline: false,
    alignment: "unset",
    lineHeight: DEFAULT_VERSE_LINE_HEIGHT,
  },
};

/**
 * Maps each text section to the theme color variable it should override.
 * Exported so the settings UI can render the resolved theme color in the
 * "follow theme" swatch.
 */
export const TEXT_SECTION_THEME_COLOR_VAR: Record<TextSectionId, string> = {
  bookTitle: "--sb-book-title-font-color",
  heading: "--sb-chapter-heading-font-color",
  verse: "--sb-verse-font-color",
};

const DEFAULT_TOOLBAR_CONFIG: ToolbarCustomization = {
  hidden: [],
  order: [],
};

const DEFAULT_SETTINGS: AppSettings = {
  fontSize: "M",
  disablePanels: false,
  bookOrientation: "traditional",
  uiSize: "M",
  selectionUI: DEFAULT_SELECTION_UI,
  scriptureElements: DEFAULT_SCRIPTURE_ELEMENTS,
  discoveredContent: DEFAULT_DISCOVERED_CONTENT,
  textConfig: DEFAULT_TEXT_CONFIG,
  toolbar: DEFAULT_TOOLBAR_CONFIG,
  keepScreenAwake: false,
  askToSwitchUiLanguage: true,
  customHighlightColors: [],
  scriptureWidth: DEFAULT_SCRIPTURE_WIDTH,
  themeId: SYSTEM_THEME_ID,
  customTheme: {},
  customHighlights: {},
  discoverContentPanelInline: true,
  verseSelectionEffects: "default",
};

function parseCustomHighlightColors(value: unknown): string[] {
  let parsed: unknown = value;
  if (typeof parsed === "string" && parsed.length > 0) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .slice(0, MAX_CUSTOM_HIGHLIGHT_COLORS);
}

function parseThemeId(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim().length > 0
    ? value
    : fallback;
}

export function parseStringRecord(value: unknown): Record<string, string> {
  let parsed: unknown = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof v === "string") {
      result[key] = v;
    }
  }
  return result;
}

function parseHighlightOverrides(
  value: unknown
): Record<string, Partial<ThemeHighlightColor>> {
  let parsed: unknown = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return {};
  }
  const obj = parsed as Record<string, unknown>;
  const overrides: Record<string, Partial<ThemeHighlightColor>> = {};
  for (const [id, entry] of Object.entries(obj)) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const sub: Partial<ThemeHighlightColor> = {};
    if (typeof e.color === "string") sub.color = e.color;
    if (typeof e.fontColor === "string") sub.fontColor = e.fontColor;
    if (typeof e.wordsOfJesusFontColor === "string") {
      sub.wordsOfJesusFontColor = e.wordsOfJesusFontColor;
    }
    if (Object.keys(sub).length > 0) overrides[id] = sub;
  }
  return overrides;
}

function parseBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return fallback;
}
function parseVerseSelectionEffects(
  value: unknown,
  fallback: VerseSelectionEffects
): VerseSelectionEffects {
  return value === "default" || value === "enabled" || value === "disabled"
    ? value
    : fallback;
}

/**
 * Apply the user's toolbar customization (hidden + explicit order) to a list
 * of tools identified by `id`. Hidden tools are removed; tools listed in
 * `order` come first in that order; remaining tools keep their natural order.
 */
export function applyToolbarCustomization<T extends { id: string }>(
  tools: T[],
  config: ToolbarCustomization
): T[] {
  const hiddenSet = new Set(config.hidden);
  const visible = tools.filter((t) => !hiddenSet.has(t.id));
  if (config.order.length === 0) {
    return visible;
  }
  const byId = new Map(visible.map((t) => [t.id, t] as const));
  const ordered: T[] = [];
  for (const id of config.order) {
    const tool = byId.get(id);
    if (tool) {
      ordered.push(tool);
      byId.delete(id);
    }
  }
  for (const tool of visible) {
    if (byId.has(tool.id)) {
      ordered.push(tool);
    }
  }
  return ordered;
}

export const UI_SIZE_OPTIONS: UISize[] = ["S", "M", "L", "XL"];

export const UI_SIZE_SCALE_MAP: Record<UISize, number> = {
  S: 0.85,
  M: 1.0,
  L: 1.15,
  XL: 1.3,
};

function parseBookOrientation(
  value: unknown,
  fallback: BookOrientation
): BookOrientation {
  return value === "tanakh" || value === "traditional" ? value : fallback;
}

function parseUISize(value: unknown, fallback: UISize): UISize {
  if (typeof value !== "string") {
    return fallback;
  }
  const normalized = value.trim().toUpperCase();
  return UI_SIZE_OPTIONS.includes(normalized as UISize)
    ? (normalized as UISize)
    : fallback;
}

function parseSelectionUI(
  value: unknown,
  fallback: SelectionUIBehavior
): SelectionUIBehavior {
  let parsed: unknown = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return fallback;
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return fallback;
  }
  const obj = parsed as Record<string, unknown>;
  return {
    showSelectedItems:
      typeof obj.showSelectedItems === "boolean"
        ? obj.showSelectedItems
        : fallback.showSelectedItems,
    showHighlightColors:
      typeof obj.showHighlightColors === "boolean"
        ? obj.showHighlightColors
        : fallback.showHighlightColors,
    showIconText:
      typeof obj.showIconText === "boolean"
        ? obj.showIconText
        : fallback.showIconText,
  };
}

function parseScriptureElements(
  value: unknown,
  fallback: ScriptureElementsBehavior
): ScriptureElementsBehavior {
  let parsed: unknown = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return fallback;
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return fallback;
  }
  const obj = parsed as Record<string, unknown>;
  return {
    showHeadings:
      typeof obj.showHeadings === "boolean"
        ? obj.showHeadings
        : fallback.showHeadings,
    showVerseNumbers:
      typeof obj.showVerseNumbers === "boolean"
        ? obj.showVerseNumbers
        : fallback.showVerseNumbers,
    showFootnotes:
      typeof obj.showFootnotes === "boolean"
        ? obj.showFootnotes
        : fallback.showFootnotes,
    showHighlights:
      typeof obj.showHighlights === "boolean"
        ? obj.showHighlights
        : fallback.showHighlights,
    showRedLettering:
      typeof obj.showRedLettering === "boolean"
        ? obj.showRedLettering
        : fallback.showRedLettering,
  };
}

function parseDiscoveredContent(
  value: unknown,
  fallback: DiscoveredContentBehavior
): DiscoveredContentBehavior {
  let parsed: unknown = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return fallback;
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return fallback;
  }
  const obj = parsed as Record<string, unknown>;
  return {
    showContent:
      typeof obj.showContent === "boolean"
        ? obj.showContent
        : fallback.showContent,
  };
}

function parseAlignment(
  value: unknown,
  fallback: TextAlignment
): TextAlignment {
  return value === "unset" ||
    value === "left" ||
    value === "center" ||
    value === "right"
    ? value
    : fallback;
}

function parseTextSection(
  value: unknown,
  fallback: TextSectionConfig
): TextSectionConfig {
  if (!value || typeof value !== "object") return fallback;
  const obj = value as Record<string, unknown>;
  return {
    font: typeof obj.font === "string" ? obj.font : fallback.font,
    weight: typeof obj.weight === "string" ? obj.weight : fallback.weight,
    color: typeof obj.color === "string" ? obj.color : fallback.color,
    marginVertical: parseNumber(obj.marginVertical, fallback.marginVertical),
    marginHorizontal: parseNumber(
      obj.marginHorizontal,
      fallback.marginHorizontal
    ),
    bold: typeof obj.bold === "boolean" ? obj.bold : fallback.bold,
    italic: typeof obj.italic === "boolean" ? obj.italic : fallback.italic,
    underline:
      typeof obj.underline === "boolean" ? obj.underline : fallback.underline,
    alignment: parseAlignment(obj.alignment, fallback.alignment),
    ...(fallback.lineHeight !== undefined || obj.lineHeight !== undefined
      ? { lineHeight: parseNumber(obj.lineHeight, fallback.lineHeight ?? 1.5) }
      : {}),
  };
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function parseToolbarConfig(
  value: unknown,
  fallback: ToolbarCustomization
): ToolbarCustomization {
  let parsed: unknown = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return fallback;
    }
  }
  if (!parsed || typeof parsed !== "object") return fallback;
  const obj = parsed as Record<string, unknown>;
  return {
    hidden: parseStringArray(obj.hidden),
    order: parseStringArray(obj.order),
  };
}

function parseTextConfig(value: unknown, fallback: TextConfig): TextConfig {
  let parsed: unknown = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return fallback;
    }
  }
  if (!parsed || typeof parsed !== "object") return fallback;
  const obj = parsed as Record<string, unknown>;
  return {
    bookTitle: parseTextSection(obj.bookTitle, fallback.bookTitle),
    heading: parseTextSection(obj.heading, fallback.heading),
    verse: parseTextSection(obj.verse, fallback.verse),
  };
}

function applyTextConfigToCSSVars(config: TextConfig) {
  if (typeof document === "undefined") return;
  const root = document.documentElement.style;
  // The theme writes `--sb-*-font-color` to `body { ... }`. Inline styles on
  // body win over CSS rules on body, so we override there. Writing to :root
  // would lose to body's own custom property.
  const body = document.body?.style ?? null;
  for (const [section, cfg] of Object.entries(config)) {
    const prefix = `--text-${section}`;
    root.setProperty(`${prefix}-font`, cfg.font);
    root.setProperty(`${prefix}-weight`, cfg.bold ? "700" : cfg.weight);
    root.setProperty(`${prefix}-font-style`, cfg.italic ? "italic" : "normal");
    root.setProperty(
      `${prefix}-text-decoration`,
      cfg.underline ? "underline" : "none"
    );
    root.setProperty(`${prefix}-alignment`, cfg.alignment);
    root.setProperty(`${prefix}-margin-top`, `${cfg.marginVertical}px`);
    root.setProperty(`${prefix}-margin-bottom`, `${cfg.marginVertical}px`);
    root.setProperty(`${prefix}-margin-left`, `${cfg.marginHorizontal}px`);
    root.setProperty(`${prefix}-margin-right`, `${cfg.marginHorizontal}px`);
    if (cfg.lineHeight !== undefined) {
      root.setProperty(`${prefix}-line-height`, String(cfg.lineHeight));
    }

    if (body) {
      const themeVar = TEXT_SECTION_THEME_COLOR_VAR[section as TextSectionId];
      if (cfg.color) {
        body.setProperty(themeVar, cfg.color);
      } else {
        body.removeProperty(themeVar);
      }
    }
  }
}

export interface SettingsManager {
  settings: Signal<AppSettings>;
  setFontSize: (fontSize: TextSize) => void;
  setDisablePanels: (disablePanels: boolean) => void;
  /**
   * Enables/disables the "switch your UI language?" prompt shown after picking
   * a translation written in another language. See
   * `AppSettings.askToSwitchUiLanguage`.
   */
  setAskToSwitchUiLanguage: (askToSwitchUiLanguage: boolean) => void;
  /**
   * Persists the user's chosen UI language to their profile. Wired into
   * `I18nManager`'s `requestLanguageChange` (the selector path) via
   * `setLanguagePersister`, so it runs ONLY for explicit selector choices —
   * never for URL-driven changes (a shared `?lang=` link or browser
   * back/forward), which stay view-only, and never for the profile-to-i18n
   * sync effect (which would just write the value straight back).
   */
  persistLanguage: (language: string) => void;
  setBookOrientation: (orientation: BookOrientation) => void;
  setUISize: (size: UISize) => void;
  setSelectionUI: (patch: Partial<SelectionUIBehavior>) => void;
  setScriptureElements: (patch: Partial<ScriptureElementsBehavior>) => void;
  setDiscoveredContent: (patch: Partial<DiscoveredContentBehavior>) => void;
  updateTextSection: (
    section: TextSectionId,
    patch: Partial<TextSectionConfig>
  ) => void;
  /** Set the max width of the chapter content (Scripture Width control). */
  setScriptureWidth: (width: number) => void;
  /** Set the verse line-height (Scripture line-spacing control). */
  setVerseLineHeight: (lineHeight: number) => void;
  /** Clear per-section color overrides so the active theme drives text colors. */
  resetTextColors: () => void;
  resetTextConfig: () => void;
  setToolbarHidden: (toolId: string, hidden: boolean) => void;
  setToolbarOrder: (order: string[]) => void;
  resetToolbarConfig: () => void;
  setKeepScreenAwake: (enabled: boolean) => void;
  addCustomHighlightColor: (color: string) => void;
  removeCustomHighlightColor: (color: string) => void;
  setAllSettings: (next: AppSettings) => void;
  resetToDefaults: () => void;
  /** Persists the selected theme preset id. Consumed by ThemeManager. */
  setThemeId: (themeId: string) => void;
  /** Persists theme color overrides. Consumed by ThemeManager. */
  setCustomTheme: (next: Record<string, string>) => void;
  /** Persists theme highlight-color overrides. Consumed by ThemeManager. */
  setCustomHighlights: (
    next: Record<string, Partial<ThemeHighlightColor>>
  ) => void;
  /**
   * Persists whether the compact discover panel sits inline beside the
   * scripture text. Consumed by `BibleReadingManager`'s per-tab
   * `discoverContentPanelInline` signal.
   */
  setDiscoverContentPanelInline: (discoverContentPanelInline: boolean) => void;
  setVerseSelectionEffects: (value: VerseSelectionEffects) => void;
}

export function createSettings(
  os: CasualOSManager,
  login: LoginManager,
  navigation: NavigationManager
): SettingsManager {
  // A per-instance, non-persisted overlay: seeded once from the URL's query
  // params at construction (deep-linking — e.g. a partner site embedding
  // Seed Bible with `?app.fontSize=XL` to preset a starting value), then
  // kept up to date by every setter below (mirroring the pre-merge
  // `configBot.tags` this replaces). It sits between `profile` and
  // `login.localConfig` in the read precedence, so a URL param only ever
  // sets the *starting* value for this session: if `readSettings()` instead
  // re-read `navigation.currentUrl.value` fresh on every call, the
  // still-present param would permanently outrank `login.localConfig`,
  // reverting every anonymous edit back to the URL's value in the same tick
  // it was made (since every setter's `saveProfileConfigValue` call writes
  // to `login.localConfig`, which the effect below reactively re-reads).
  const sessionOverrides: Record<string, unknown> = Object.fromEntries(
    navigation.currentUrl.value.searchParams
  );
  // A boolean, not the URL itself, so `readSettings()` (and every reader of
  // `settings`) reruns only when embed mode flips, not on every navigation.
  const isMinimalEmbed = computed(() =>
    isMinimalEmbedUrl(navigation.currentUrl.value)
  );

  // Read each setting with the precedence: user profile > this session's
  // URL/override cache > `login.localConfig` (the anonymous, device-only
  // store shared with every other config/settings caller via
  // `saveProfileConfigValue`) > preset/default. The profile is the source
  // of truth once logged in; `login.localConfig` covers anonymous use and
  // offline bootstrapping before the profile loads — every setter below
  // already writes there via `saveProfileConfigValue`, so reading it back
  // here is what makes anonymous edits survive a refresh.
  const readSettings = (): AppSettings => {
    const profile = login.profile.value;
    const local = login.localConfig.value;
    const settingsPreset = parseSettingsPreset(sessionOverrides.settingsPreset);
    const presetConfig = getPresetConfig(settingsPreset);

    const read = (profileKey: string, urlKey: string): unknown =>
      getProfileConfigValue(profile, profileKey) ??
      sessionOverrides[urlKey] ??
      local[profileKey];

    // The embed link's theme is the look the site owner chose. The viewer's
    // saved theme and color edits would otherwise paint over it, so the
    // preview (and every signed-in visitor) would ignore the link.
    const embedTheme = sessionOverrides[TAG_THEME_ID];
    const pinnedEmbedTheme =
      isMinimalEmbed.value &&
      typeof embedTheme === "string" &&
      embedTheme.trim().length > 0
        ? embedTheme.trim()
        : undefined;

    return {
      fontSize: parseFontSize(
        read(PROFILE_FONT_SIZE, TAG_FONT_SIZE),
        presetConfig.fontSize
      ),
      disablePanels: parseBoolean(
        read(PROFILE_DISABLE_PANELS, TAG_DISABLE_PANELS),
        presetConfig.disablePanels
      ),
      bookOrientation: parseBookOrientation(
        read(PROFILE_BOOK_ORIENTATION, TAG_BOOK_ORIENTATION),
        DEFAULT_SETTINGS.bookOrientation
      ),
      uiSize: parseUISize(
        read(PROFILE_UI_SIZE, TAG_UI_SIZE),
        DEFAULT_SETTINGS.uiSize
      ),
      selectionUI: parseSelectionUI(
        read(PROFILE_SELECTION_UI, TAG_SELECTION_UI),
        DEFAULT_SETTINGS.selectionUI
      ),
      scriptureElements: parseScriptureElements(
        read(PROFILE_SCRIPTURE_ELEMENTS, TAG_SCRIPTURE_ELEMENTS),
        DEFAULT_SETTINGS.scriptureElements
      ),
      discoveredContent: parseDiscoveredContent(
        read(PROFILE_DISCOVERED_CONTENT, TAG_DISCOVERED_CONTENT),
        DEFAULT_SETTINGS.discoveredContent
      ),
      textConfig: parseTextConfig(
        read(PROFILE_TEXT_CONFIG, TAG_TEXT_CONFIG),
        DEFAULT_SETTINGS.textConfig
      ),
      verseSelectionEffects: parseVerseSelectionEffects(
        read(PROFILE_VERSE_SELECTION_EFFECTS, TAG_VERSE_SELECTION_EFFECTS),
        DEFAULT_SETTINGS.verseSelectionEffects ?? "default"
      ),
      toolbar: parseToolbarConfig(
        read(PROFILE_TOOLBAR, TAG_TOOLBAR),
        DEFAULT_SETTINGS.toolbar
      ),
      keepScreenAwake: parseBoolean(
        read(PROFILE_KEEP_AWAKE, TAG_KEEP_AWAKE),
        DEFAULT_SETTINGS.keepScreenAwake
      ),
      askToSwitchUiLanguage: parseBoolean(
        read(PROFILE_ASK_TO_SWITCH_UI_LANGUAGE, TAG_ASK_TO_SWITCH_UI_LANGUAGE),
        DEFAULT_SETTINGS.askToSwitchUiLanguage
      ),
      customHighlightColors: parseCustomHighlightColors(
        read(PROFILE_CUSTOM_HIGHLIGHT_COLORS, TAG_CUSTOM_HIGHLIGHT_COLORS)
      ),
      scriptureWidth: parseNumber(
        read(PROFILE_SCRIPTURE_WIDTH, TAG_SCRIPTURE_WIDTH),
        DEFAULT_SETTINGS.scriptureWidth
      ),
      themeId: parseThemeId(
        pinnedEmbedTheme ?? read(PROFILE_THEME_ID, TAG_THEME_ID),
        DEFAULT_SETTINGS.themeId
      ),
      customTheme: pinnedEmbedTheme
        ? {}
        : parseStringRecord(read(PROFILE_CUSTOM_THEME, TAG_CUSTOM_THEME)),
      customHighlights: pinnedEmbedTheme
        ? {}
        : parseHighlightOverrides(
            read(PROFILE_CUSTOM_HIGHLIGHTS, TAG_CUSTOM_HIGHLIGHTS)
          ),
      discoverContentPanelInline: parseBoolean(
        read(
          PROFILE_DISCOVER_CONTENT_PANEL_INLINE,
          TAG_DISCOVER_CONTENT_PANEL_INLINE
        ),
        DEFAULT_SETTINGS.discoverContentPanelInline
      ),
    };
  };

  const settings = signal<AppSettings>(readSettings());

  // Re-read whenever the profile or the anonymous local config changes —
  // `readSettings()` reads both, so this effect stays in sync with
  // login/logout and anonymous edits (which land in `login.localConfig` via
  // `saveProfileConfigValue`). Safe to also depend on `login.localConfig`
  // now: `sessionOverrides` (above), not a fresh URL read, is what sits
  // above it in precedence, and setters keep both in sync, so a re-read
  // triggered by this effect can no longer revert a same-session edit.
  effect(() => {
    settings.value = readSettings();
  });

  const setFontSize = (fontSize: TextSize) => {
    const nextFontSize = parseFontSize(fontSize, settings.value.fontSize);
    settings.value = { ...settings.value, fontSize: nextFontSize };
    sessionOverrides[TAG_FONT_SIZE] = nextFontSize;
    saveProfileConfigValue(login, PROFILE_FONT_SIZE, nextFontSize);
  };

  const setDisablePanels = (disablePanels: boolean) => {
    settings.value = { ...settings.value, disablePanels };
    sessionOverrides[TAG_DISABLE_PANELS] = disablePanels;
    saveProfileConfigValue(login, PROFILE_DISABLE_PANELS, disablePanels);
  };

  const setAskToSwitchUiLanguage = (askToSwitchUiLanguage: boolean) => {
    settings.value = { ...settings.value, askToSwitchUiLanguage };
    sessionOverrides[TAG_ASK_TO_SWITCH_UI_LANGUAGE] = askToSwitchUiLanguage;
    saveProfileConfigValue(
      login,
      PROFILE_ASK_TO_SWITCH_UI_LANGUAGE,
      askToSwitchUiLanguage
    );
  };

  // Apply the profile's saved UI language, but ONLY when the profile itself
  // changes (i.e. on login / profile load) — deliberately NOT on every URL
  // change.
  //
  // The `?lang=` query param is owned by I18nManager (see its
  // `syncSignalsToUrl({ lang })`), which is the single source of truth for
  // the URL <-> `i18n.language` relationship. If this ran on URL changes
  // too, it would fight the user's in-session language switch: picking a
  // language writes `?lang=` first, that URL write would re-run this
  // effect, and the profile's still-unsaved previous language would revert
  // `i18n.language` (leaving only `?translation=` applied). Only read
  // `login.profile` here so the effect subscribes to the profile alone.
  effect(() => {
    const profileLanguage = getProfileConfigValue(login.profile.value, "lang");
    const nextLanguage =
      typeof profileLanguage === "string" && profileLanguage.trim().length > 0
        ? profileLanguage
        : null;

    if (nextLanguage && nextLanguage !== i18n.language) {
      void i18n.changeLanguage(nextLanguage);
    }
  });

  const persistLanguage = (language: string) => {
    void saveProfileConfigValue(login, "lang", language);
  };

  const setBookOrientation = (orientation: BookOrientation) => {
    settings.value = { ...settings.value, bookOrientation: orientation };
    sessionOverrides[TAG_BOOK_ORIENTATION] = orientation;
    saveProfileConfigValue(login, PROFILE_BOOK_ORIENTATION, orientation);
  };

  const setUISize = (size: UISize) => {
    settings.value = { ...settings.value, uiSize: size };
    sessionOverrides[TAG_UI_SIZE] = size;
    saveProfileConfigValue(login, PROFILE_UI_SIZE, size);
  };

  const setSelectionUI = (patch: Partial<SelectionUIBehavior>) => {
    const next = { ...settings.value.selectionUI, ...patch };
    settings.value = { ...settings.value, selectionUI: next };
    sessionOverrides[TAG_SELECTION_UI] = next;
    saveProfileConfigValue(login, PROFILE_SELECTION_UI, next);
  };

  const setScriptureElements = (patch: Partial<ScriptureElementsBehavior>) => {
    const next = { ...settings.value.scriptureElements, ...patch };
    settings.value = { ...settings.value, scriptureElements: next };
    sessionOverrides[TAG_SCRIPTURE_ELEMENTS] = next;
    saveProfileConfigValue(login, PROFILE_SCRIPTURE_ELEMENTS, next);
  };

  const setDiscoveredContent = (patch: Partial<DiscoveredContentBehavior>) => {
    const next = { ...settings.value.discoveredContent, ...patch };
    settings.value = { ...settings.value, discoveredContent: next };
    sessionOverrides[TAG_DISCOVERED_CONTENT] = next;
    saveProfileConfigValue(login, PROFILE_DISCOVERED_CONTENT, next);
  };

  const writeTextConfig = (next: TextConfig) => {
    settings.value = { ...settings.value, textConfig: next };
    sessionOverrides[TAG_TEXT_CONFIG] = next;
    saveProfileConfigValue(login, PROFILE_TEXT_CONFIG, next);
  };

  const updateTextSection = (
    section: TextSectionId,
    patch: Partial<TextSectionConfig>
  ) => {
    const nextSection = { ...settings.value.textConfig[section], ...patch };
    const nextTextConfig = {
      ...settings.value.textConfig,
      [section]: nextSection,
    };
    writeTextConfig(nextTextConfig);
  };

  const setScriptureWidth = (width: number) => {
    if (!Number.isFinite(width)) return;
    const clamped = Math.max(
      MIN_SCRIPTURE_WIDTH,
      Math.min(MAX_SCRIPTURE_WIDTH, width)
    );
    settings.value = { ...settings.value, scriptureWidth: clamped };
    sessionOverrides[TAG_SCRIPTURE_WIDTH] = clamped;
    saveProfileConfigValue(login, PROFILE_SCRIPTURE_WIDTH, clamped);
  };
  const setVerseSelectionEffects = (value: VerseSelectionEffects) => {
    settings.value = {
      ...settings.value,
      verseSelectionEffects: value,
    };

    sessionOverrides[TAG_VERSE_SELECTION_EFFECTS] = value;

    saveProfileConfigValue(login, PROFILE_VERSE_SELECTION_EFFECTS, value);
  };

  const setVerseLineHeight = (lineHeight: number) => {
    if (!Number.isFinite(lineHeight)) return;
    const current = settings.value.textConfig;
    const nextTextConfig: TextConfig = {
      ...current,
      verse: { ...current.verse, lineHeight },
    };
    writeTextConfig(nextTextConfig);
  };

  const resetTextConfig = () => {
    settings.value = { ...settings.value, textConfig: DEFAULT_TEXT_CONFIG };
    sessionOverrides[TAG_TEXT_CONFIG] = DEFAULT_TEXT_CONFIG;
    saveProfileConfigValue(login, PROFILE_TEXT_CONFIG, DEFAULT_TEXT_CONFIG);
  };

  const resetTextColors = () => {
    const current = settings.value.textConfig;
    let changed = false;
    const next = {} as TextConfig;
    for (const section of Object.keys(current) as TextSectionId[]) {
      const cfg = current[section];
      if (cfg.color !== "") {
        changed = true;
        next[section] = { ...cfg, color: "" };
      } else {
        next[section] = cfg;
      }
    }
    if (!changed) return;
    writeTextConfig(next);
  };

  const writeToolbarConfig = (next: ToolbarCustomization) => {
    settings.value = { ...settings.value, toolbar: next };
    sessionOverrides[TAG_TOOLBAR] = next;
    saveProfileConfigValue(login, PROFILE_TOOLBAR, next);
  };

  const setToolbarHidden = (toolId: string, hidden: boolean) => {
    const current = settings.value.toolbar;
    const hiddenSet = new Set(current.hidden);
    if (hidden) hiddenSet.add(toolId);
    else hiddenSet.delete(toolId);
    writeToolbarConfig({ ...current, hidden: [...hiddenSet] });
  };

  const setToolbarOrder = (order: string[]) => {
    writeToolbarConfig({ ...settings.value.toolbar, order });
  };

  const resetToolbarConfig = () => {
    writeToolbarConfig(DEFAULT_TOOLBAR_CONFIG);
  };

  const setKeepScreenAwake = async (enabled: boolean) => {
    if (settings.value.keepScreenAwake === enabled) return;
    let nextValue = enabled;
    if (enabled) {
      try {
        await os.requestWakeLock();
      } catch {
        nextValue = false;
      }
    } else {
      os.disableWakeLock();
    }
    settings.value = { ...settings.value, keepScreenAwake: nextValue };
    sessionOverrides[TAG_KEEP_AWAKE] = nextValue;
    saveProfileConfigValue(login, PROFILE_KEEP_AWAKE, nextValue);
  };

  const writeCustomHighlightColors = (colors: string[]) => {
    settings.value = { ...settings.value, customHighlightColors: colors };
    sessionOverrides[TAG_CUSTOM_HIGHLIGHT_COLORS] = colors;
    saveProfileConfigValue(login, PROFILE_CUSTOM_HIGHLIGHT_COLORS, colors);
  };

  // Next ring-buffer slot to overwrite once all 3 custom colours are filled.
  // Empty/partial palettes always append, so this only matters at the cap:
  // the 4th pick replaces slot 0 (the 1st), the 5th replaces slot 1 (the 2nd).
  let nextCustomHighlightSlot = 0;

  const addCustomHighlightColor = (color: string) => {
    const normalized = color.trim().toLowerCase();
    if (!normalized) return;
    const current = settings.value.customHighlightColors;
    if (current.some((c) => c.toLowerCase() === normalized)) {
      return;
    }
    if (current.length < MAX_CUSTOM_HIGHLIGHT_COLORS) {
      writeCustomHighlightColors([...current, normalized]);
      nextCustomHighlightSlot = 0;
      return;
    }
    const next = [...current];
    next[nextCustomHighlightSlot] = normalized;
    nextCustomHighlightSlot =
      (nextCustomHighlightSlot + 1) % MAX_CUSTOM_HIGHLIGHT_COLORS;
    writeCustomHighlightColors(next);
  };

  const removeCustomHighlightColor = (color: string) => {
    const normalized = color.trim().toLowerCase();
    writeCustomHighlightColors(
      settings.value.customHighlightColors.filter(
        (c) => c.toLowerCase() !== normalized
      )
    );
    nextCustomHighlightSlot = 0;
  };

  const setThemeId = (themeId: string) => {
    settings.value = { ...settings.value, themeId };
    sessionOverrides[TAG_THEME_ID] = themeId;
    saveProfileConfigValue(login, PROFILE_THEME_ID, themeId);
  };

  const setCustomTheme = (next: Record<string, string>) => {
    settings.value = { ...settings.value, customTheme: next };
    sessionOverrides[TAG_CUSTOM_THEME] = next;
    saveProfileConfigValue(login, PROFILE_CUSTOM_THEME, next);
  };

  const setCustomHighlights = (
    next: Record<string, Partial<ThemeHighlightColor>>
  ) => {
    settings.value = { ...settings.value, customHighlights: next };
    sessionOverrides[TAG_CUSTOM_HIGHLIGHTS] = next;
    saveProfileConfigValue(login, PROFILE_CUSTOM_HIGHLIGHTS, next);
  };

  const setDiscoverContentPanelInline = (
    discoverContentPanelInline: boolean
  ) => {
    settings.value = { ...settings.value, discoverContentPanelInline };
    sessionOverrides[TAG_DISCOVER_CONTENT_PANEL_INLINE] =
      discoverContentPanelInline;
    saveProfileConfigValue(
      login,
      PROFILE_DISCOVER_CONTENT_PANEL_INLINE,
      discoverContentPanelInline
    );
  };

  const setAllSettings = (next: AppSettings) => {
    next = AppSettingsSchema.parse(next);
    settings.value = next;
    if (login.userId.value) {
      const existingProfile = login.profile.value;
      login.updateProfile({
        config: {
          ...(existingProfile?.config ?? {}),
          ...next,
        },
      });
    }
  };

  const resetToDefaults = () => {
    nextCustomHighlightSlot = 0;
    settings.value = DEFAULT_SETTINGS;
    sessionOverrides[TAG_FONT_SIZE] = DEFAULT_SETTINGS.fontSize;
    sessionOverrides[TAG_DISABLE_PANELS] = DEFAULT_SETTINGS.disablePanels;
    sessionOverrides[TAG_BOOK_ORIENTATION] = DEFAULT_SETTINGS.bookOrientation;
    sessionOverrides[TAG_UI_SIZE] = DEFAULT_SETTINGS.uiSize;
    sessionOverrides[TAG_SELECTION_UI] = DEFAULT_SETTINGS.selectionUI;
    sessionOverrides[TAG_SCRIPTURE_ELEMENTS] =
      DEFAULT_SETTINGS.scriptureElements;
    sessionOverrides[TAG_DISCOVERED_CONTENT] =
      DEFAULT_SETTINGS.discoveredContent;
    sessionOverrides[TAG_TEXT_CONFIG] = DEFAULT_SETTINGS.textConfig;
    sessionOverrides[TAG_TOOLBAR] = DEFAULT_SETTINGS.toolbar;
    sessionOverrides[TAG_KEEP_AWAKE] = DEFAULT_SETTINGS.keepScreenAwake;
    sessionOverrides[TAG_ASK_TO_SWITCH_UI_LANGUAGE] =
      DEFAULT_SETTINGS.askToSwitchUiLanguage;
    sessionOverrides[TAG_CUSTOM_HIGHLIGHT_COLORS] = [];
    sessionOverrides[TAG_SCRIPTURE_WIDTH] = DEFAULT_SETTINGS.scriptureWidth;
    sessionOverrides[TAG_THEME_ID] = DEFAULT_SETTINGS.themeId;
    sessionOverrides[TAG_CUSTOM_THEME] = {};
    sessionOverrides[TAG_CUSTOM_HIGHLIGHTS] = {};
    sessionOverrides[TAG_DISCOVER_CONTENT_PANEL_INLINE] =
      DEFAULT_SETTINGS.discoverContentPanelInline;
    sessionOverrides[TAG_VERSE_SELECTION_EFFECTS] =
      DEFAULT_SETTINGS.verseSelectionEffects ?? "default";

    saveProfileConfigValue(login, PROFILE_FONT_SIZE, DEFAULT_SETTINGS.fontSize);
    saveProfileConfigValue(
      login,
      PROFILE_DISABLE_PANELS,
      DEFAULT_SETTINGS.disablePanels
    );
    saveProfileConfigValue(
      login,
      PROFILE_BOOK_ORIENTATION,
      DEFAULT_SETTINGS.bookOrientation
    );
    saveProfileConfigValue(login, PROFILE_UI_SIZE, DEFAULT_SETTINGS.uiSize);
    saveProfileConfigValue(
      login,
      PROFILE_SELECTION_UI,
      DEFAULT_SETTINGS.selectionUI
    );
    saveProfileConfigValue(
      login,
      PROFILE_SCRIPTURE_ELEMENTS,
      DEFAULT_SETTINGS.scriptureElements
    );
    saveProfileConfigValue(
      login,
      PROFILE_DISCOVERED_CONTENT,
      DEFAULT_SETTINGS.discoveredContent
    );
    saveProfileConfigValue(
      login,
      PROFILE_VERSE_SELECTION_EFFECTS,
      DEFAULT_SETTINGS.verseSelectionEffects ?? "default"
    );
    saveProfileConfigValue(
      login,
      PROFILE_TEXT_CONFIG,
      DEFAULT_SETTINGS.textConfig
    );
    saveProfileConfigValue(login, PROFILE_TOOLBAR, DEFAULT_SETTINGS.toolbar);
    saveProfileConfigValue(
      login,
      PROFILE_KEEP_AWAKE,
      DEFAULT_SETTINGS.keepScreenAwake
    );
    saveProfileConfigValue(
      login,
      PROFILE_ASK_TO_SWITCH_UI_LANGUAGE,
      DEFAULT_SETTINGS.askToSwitchUiLanguage
    );
    saveProfileConfigValue(login, PROFILE_CUSTOM_HIGHLIGHT_COLORS, []);
    saveProfileConfigValue(
      login,
      PROFILE_SCRIPTURE_WIDTH,
      DEFAULT_SETTINGS.scriptureWidth
    );
    saveProfileConfigValue(login, PROFILE_THEME_ID, DEFAULT_SETTINGS.themeId);
    saveProfileConfigValue(login, PROFILE_CUSTOM_THEME, {});
    saveProfileConfigValue(login, PROFILE_CUSTOM_HIGHLIGHTS, {});
    saveProfileConfigValue(
      login,
      PROFILE_DISCOVER_CONTENT_PANEL_INLINE,
      DEFAULT_SETTINGS.discoverContentPanelInline
    );
  };

  // Scale UI surfaces via `--sb-ui-scale`, which drives `html { font-size }`
  // (see app/styles/base.css). Chrome sized in `rem` tracks this; reader text keeps its
  // own font-size knob (`.sb-bible-reader` carries `.sb-font-size-*`), so it
  // stays independent without the old root-`zoom` + counter-zoom hack.
  effect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const scale = UI_SIZE_SCALE_MAP[settings.value.uiSize];
    document.documentElement.style.setProperty("--sb-ui-scale", String(scale));
  });

  // Publish per-section text config as CSS variables (`--text-<section>-*`)
  // so reader styles can consume typography preferences.
  effect(() => {
    applyTextConfigToCSSVars(settings.value.textConfig);
  });

  // Publish the scripture margin (%) as a CSS variable consumed by
  // `.sb-bible-reader`'s horizontal padding.
  effect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty(
      "--sb-scripture-width",
      `${settings.value.scriptureWidth}ch`
    );
  });

  // Keep the OS wake-lock in sync with the persisted setting. Survives
  // SettingsPage mount/unmount so re-opening settings shows the real state.
  effect(() => {
    const enabled = settings.value.keepScreenAwake;
    if (enabled) {
      void os.requestWakeLock();
    } else {
      void os.disableWakeLock();
    }
  });

  return {
    settings,
    setFontSize,
    setDisablePanels,
    setAskToSwitchUiLanguage,
    persistLanguage,
    setBookOrientation,
    setUISize: setUISize,
    setSelectionUI,
    setScriptureElements,
    setDiscoveredContent,
    updateTextSection,
    setScriptureWidth,
    setVerseLineHeight,
    resetTextColors,
    resetTextConfig,
    setToolbarHidden,
    setToolbarOrder,
    resetToolbarConfig,
    setKeepScreenAwake,
    addCustomHighlightColor,
    removeCustomHighlightColor,
    setAllSettings,
    resetToDefaults,
    setThemeId,
    setCustomTheme,
    setCustomHighlights,
    setDiscoverContentPanelInline,
    setVerseSelectionEffects,
  };
}
